import { Component, Fragment, type ErrorInfo, type ReactNode } from "react";
import { Alert, Button, Code, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { openInspector } from "../lib/calls";

interface Props {
  children: ReactNode;
  /** Shown in the title, e.g. "This page" or "Client settings". */
  label?: string;
  /** Inspector access (only inside the signed-in demo, where calls are recorded). */
  inspector?: boolean;
}

/**
 * Keeps a render error (usually an unexpected API response shape) inside one area instead of
 * blanking the whole app. "Try again" remounts the children, so state that caused the error is dropped.
 */
export class ErrorBoundary extends Component<Props, { error: Error | null; attempt: number }> {
  state = { error: null as Error | null, attempt: 0 };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />} title={`${this.props.label ?? "This page"} hit an error`}>
        <Stack gap={8}>
          <Text fz="sm">Something in the last response wasn't in the shape this screen expects. The raw response is in the inspector.</Text>
          <Code block>{error.message}</Code>
          <Group gap="xs">
            {this.props.inspector && (
              <Button size="xs" variant="default" onClick={() => openInspector(null)}>
                Inspect last call
              </Button>
            )}
            <Button size="xs" variant="light" color="ink" onClick={() => this.setState((s) => ({ error: null, attempt: s.attempt + 1 }))}>
              Try again
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  }
}
